import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "classnames";
import { toast } from "react-toastify";
import CheckVerifiedIcon from "../../shared/icons/CheckVerifiedIcon";
import CloseIcon from "../../shared/icons/CloseIcon";
import UncheckCircleIcon from "../../shared/icons/UncheckCircleIcon";
import CircularProgress from "../../custom/circular-progress/CircularProgress";
import CustomButton from "../../custom/custom-button/CustomButton";
import CustomInput from "../../custom/custom-input/CustomInput";
import { User } from "../../../services";
import { useUserData } from "../../../hooks/useUserData";
import { validateEmail } from "../../../utils/checkEmail";
import style from "./index.module.scss";

const EMPTY_CODE = ["", "", "", ""];

const ConfirmEmail = ({ user, setIsOpenEmail, setIsOpenDocuments }) => {
  const { t } = useTranslation();
  const { refreshUserData } = useUserData();
  const inputs = useRef([]);
  const [email, setEmail] = useState(user?.email || "");
  const [code, setCode] = useState(EMPTY_CODE);
  const [errorTitleEmail, setErrorTitleEmail] = useState();
  const [isCodeSent, setIsCodeSent] = useState(false);
  const [isOpenUpdateEmail, setIsOpenUpdateEmail] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingUpdateEmail, setIsLoadingUpdateEmail] = useState(false);

  const isCodeValid = code.every(Boolean);

  const updateUserData = async () => {
    try {
      await refreshUserData();
    } catch (error) {
      console.error(error);
    }
  };

  const handleChange = (value, index) => {
    if (value === "") {
      setCode((currentCode) => currentCode.map((digit, digitIndex) => (digitIndex === index ? "" : digit)));
      return;
    }
    if (!/^\d$/.test(value)) return;

    setCode((currentCode) => currentCode.map((digit, digitIndex) => (digitIndex === index ? value : digit)));
    inputs.current[index + 1]?.focus();
  };

  const handleKeyDown = (event, index) => {
    if (event.key === "Backspace" && !code[index]) {
      inputs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (event) => {
    event.preventDefault();
    const digits = event.clipboardData.getData("text").replace(/\D/g, "").slice(0, 4).split("");
    if (digits.length === 0) return;

    setCode(EMPTY_CODE.map((_, index) => digits[index] || ""));
    inputs.current[Math.min(digits.length, 3)]?.focus();
  };

  const handleSendCode = async () => {
    if (user.emailConfirmed) {
      toast.info(t("confirmEmailComponent.toast.alreadyConfirmed"));
      return;
    }

    setIsLoading(true);
    try {
      await User.sendConfirmationEmail(email);
      toast.info(t("confirmEmailComponent.toast.codeSent"));
      setIsCodeSent(true);
    } catch (error) {
      console.error("Ошибка при отправке кода:", error);
      toast.error(t("confirmEmailComponent.toast.codeSendError"));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeEmail = async () => {
    setIsLoading(true);
    try {
      const response = await User.checkCodeEmail(code.join(""), email);
      if (response.data.isSucccess) {
        setIsCodeSent(false);
        setIsOpenEmail(false);
        setIsOpenDocuments(true);
        await updateUserData();
      }
    } catch (error) {
      console.error("Введен неправильный код подтверждения:", error);
      toast.error(t("confirmEmailComponent.toast.invalidCode"));
    } finally {
      setIsLoading(false);
    }
  };

  const updateUserEmail = async () => {
    setIsLoadingUpdateEmail(true);
    setErrorTitleEmail(undefined);

    if (!validateEmail(email)) {
      setErrorTitleEmail(t("confirmEmailComponent.validation.invalidEmailFormat"));
      setIsLoadingUpdateEmail(false);
      return;
    }

    try {
      const response = await User.check("email", email);
      if (response.data.isExists) {
        const message = t("oldRegistrationForm.validation.emailExists");
        toast.error(message);
        setErrorTitleEmail(message);
        return;
      }

      await User.updateEmail(email);
      toast.success(t("confirmEmailComponent.toast.emailChangeVerificationSent", { email }), { autoClose: 6000 });
      setIsOpenUpdateEmail(false);
      setIsCodeSent(true);
    } catch (error) {
      console.error("Error updating email:", error);
      toast.error(t("confirmEmailComponent.toast.emailUpdateError"));
    } finally {
      setIsLoadingUpdateEmail(false);
    }
  };

  const handleModalVerifiedClose = () => {
    setIsOpenEmail(false);
    setIsOpenDocuments(true);
  };

  const handleContinue = () => {
    if (user.emailConfirmed) {
      setIsOpenEmail(false);
      setIsOpenDocuments(true);
      return;
    }

    handleCodeEmail();
  };

  return (
    <>
      <section className={style.card}>
        <div className={style.card_upper}>
          <div className={style.card_head}>
            {user.emailConfirmed ? <CheckVerifiedIcon width={24} height={24} /> : <UncheckCircleIcon />}
            <h2 className={style.card_title}>{t("confirmEmailComponent.card.title")}</h2>
          </div>
          <p className={style.card_text}>
            {user.emailConfirmed
              ? t("confirmEmailComponent.card.textConfirmed")
              : t("confirmEmailComponent.card.textPending", { email })}
          </p>
        </div>

        <label className={style.emailField}>
          <span>{t("confirmEmailComponent.emailLabel")}</span>
          <input value={email} readOnly />
        </label>

        {isCodeSent ? (
          <div className={style.codeSection}>
            <div className={style.codeLabel}>{t("confirmEmailComponent.codeModal.title")}</div>
            <div className={style.codeInputs}>
              {code.map((digit, index) => (
                <input
                  key={index}
                  ref={(element) => {
                    inputs.current[index] = element;
                  }}
                  value={digit}
                  type="text"
                  inputMode="numeric"
                  maxLength={1}
                  aria-label={t("confirmEmailComponent.codeDigitLabel", { digit: index + 1 })}
                  onChange={(event) => handleChange(event.target.value, index)}
                  onKeyDown={(event) => handleKeyDown(event, index)}
                  onPaste={handlePaste}
                  className={style.codeInput}
                />
              ))}
              {code.some(Boolean) ? (
                <CircularProgress
                  size={24}
                  strokeWidth={2}
                  stages={4}
                  currentStage={code.filter(Boolean).length}
                  color="#2857FC"
                />
              ) : null}
            </div>
            <div className={style.card_lower}>
              <CustomButton
                text={t("confirmEmailComponent.buttons.resendCode")}
                onClick={handleSendCode}
                isUnderline
              />
              <CustomButton
                text={t("confirmEmailComponent.buttons.changeEmail")}
                isUnderline
                onClick={() => setIsOpenUpdateEmail(true)}
              />
            </div>
          </div>
        ) : (
          <div className={style.card_lower}>
            <CustomButton
              text={t("confirmEmailComponent.buttons.getCode")}
              onClick={handleSendCode}
              isLoading={isLoading}
              isDisabled={user.emailConfirmed}
              isMain
            />
            <CustomButton
              text={t("confirmEmailComponent.buttons.changeEmail")}
              isUnderline
              onClick={() => setIsOpenUpdateEmail(true)}
            />
          </div>
        )}

        <div className={style.continueButton}>
          <CustomButton
            text={t("common.confirm")}
            onClick={handleContinue}
            isLoading={isLoading}
            isFall
            isDisabled={isLoading || (!user.emailConfirmed && (!isCodeSent || !isCodeValid))}
          />
        </div>
      </section>

      {isOpenUpdateEmail ? (
        <div className={cn("modal-overlay", style.modal)}>
          <div className={cn(style.modal_content, style.paddingBt)}>
            <div className={style.modal_text}>
              <div className={style.modal_title}>
                {t("confirmEmailComponent.updateEmailModal.title")}
                <CloseIcon fill="#6D7885" onClick={() => setIsOpenUpdateEmail(false)} />
              </div>
              <div className={style.modal_description}>
                {t("confirmEmailComponent.updateEmailModal.description")}
              </div>
            </div>
            <CustomInput
              placeholder={t("confirmEmailComponent.updateEmailModal.emailPlaceholder")}
              labelText={t("confirmEmailComponent.updateEmailModal.emailLabel")}
              inputType="text"
              onChangeValue={setEmail}
              value={email}
              errorText={errorTitleEmail}
              isDeleteText
            />
            <div className={style.modal_btns}>
              <CustomButton
                text={t("confirmEmailComponent.buttons.getCode")}
                onClick={updateUserEmail}
                isLoading={isLoadingUpdateEmail}
                isFall
              />
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ConfirmEmail;
